import { useEffect, useState } from "react";
import { PageHeader, EmptyState } from "../components/EmptyState";
import { api, type SleepResponse } from "../lib/api";

function clock(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${h}h ${m}m`;
}

export default function Sleep() {
  const [data, setData] = useState<SleepResponse | null | "error">(null);

  useEffect(() => {
    api.sleep(14).then(setData).catch(() => setData("error"));
  }, []);

  const nights = data && data !== "error" ? data.nights : [];
  const lastNight = data && data !== "error" ? data.last_night : null;
  const hasNights = nights.length > 0;

  return (
    <div className="mx-auto max-w-2xl animate-fade-in">
      <PageHeader title="Sleep" eyebrow="Last night · stages · trend" />

      {lastNight && (
        <div className="card card-pad">
          <div className="eyebrow">
            Last night · {clock(lastNight.start_time)} – {clock(lastNight.end_time)}
            {(lastNight.pieces ?? 1) > 1 ? ` · ${lastNight.pieces} parts` : ""}
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <div className="font-display tabular-nums text-4xl">
              {formatDuration(lastNight.duration_min)}
            </div>
            {lastNight.score != null && (
              <span className="pill-positive font-display tabular-nums">
                {lastNight.score}
              </span>
            )}
          </div>

          <div className="mt-6 grid grid-cols-4 gap-3">
            <div>
              <div className="field-label">Light</div>
              <div className="font-display tabular-nums text-lg">
                {lastNight.light_min != null ? `${lastNight.light_min}m` : "—"}
              </div>
            </div>
            <div>
              <div className="field-label">Deep</div>
              <div className="font-display tabular-nums text-lg">
                {lastNight.deep_min != null ? `${lastNight.deep_min}m` : "—"}
              </div>
            </div>
            <div>
              <div className="field-label">REM</div>
              <div className="font-display tabular-nums text-lg">
                {lastNight.rem_min != null ? `${lastNight.rem_min}m` : "—"}
              </div>
            </div>
            <div>
              <div className="field-label">Awake</div>
              <div className="font-display tabular-nums text-lg">
                {lastNight.awake_min != null ? `${lastNight.awake_min}m` : "—"}
              </div>
            </div>
          </div>
        </div>
      )}

      <h2 className="section-title mt-8 mb-3">Recent nights</h2>

      {hasNights ? (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Asleep</th>
                <th>Duration</th>
                <th>Score</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {nights.map((night) => (
                <tr key={night.date}>
                  <td>{night.date}</td>
                  <td className="tabular-nums text-ink-subtle">
                    {clock(night.start_time)} – {clock(night.end_time)}
                    {(night.pieces ?? 1) > 1 && <span className="pill-neutral ml-2">{night.pieces} parts</span>}
                  </td>
                  <td className="font-display tabular-nums">
                    {formatDuration(night.duration_min)}
                  </td>
                  <td className="font-display tabular-nums">
                    {night.score != null ? night.score : <span className="muted">—</span>}
                  </td>
                  <td>
                    <span className="pill-neutral">{night.source}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState error={data === "error"} title="No sleep data yet">
          Sleep sessions from the Galaxy Watch arrive once the Health Connect bridge is
          running, or from Garmin once connected.
        </EmptyState>
      )}
    </div>
  );
}
